/*
 * <logo-marquee> — Trust Logo Bar
 * Repeats the authored logo set until it fills the viewport, then duplicates
 * that filled "half" so the CSS animation can translate by -50% for a
 * seamless loop. Speed stays constant regardless of how many logos exist.
 *
 * Progressive enhancement: without JS (or with prefers-reduced-motion) the
 * logos fall back to a static, centered, wrapping row (see CSS). The animated
 * state is set up in enhance() and fully torn down in teardown(), so toggling
 * reduced-motion at runtime flips cleanly between the two.
 */
if (!customElements.get('logo-marquee')) {
  customElements.define(
    'logo-marquee',
    class LogoMarquee extends HTMLElement {
      connectedCallback() {
        this.viewport = this.querySelector('.jk-logo-bar__viewport');
        this.track = this.querySelector('.jk-logo-bar__track');
        if (!this.track || this.track.children.length === 0) return;

        this.speed = Math.max(1, parseFloat(this.dataset.speed) || 40); // px/sec
        this.reverse = this.dataset.direction === 'right';
        this.pauseHover = this.dataset.pauseHover === 'true';

        this.originalHTML = this.track.innerHTML;
        this.originalCount = this.track.children.length;
        this.hovered = false;
        this.offscreen = false;
        this.focused = false;
        this.enhanced = false;
        this.onResize = this.debounce(() => this.build(), 200);

        // Respect prefers-reduced-motion now AND when it changes at runtime.
        this.reducedMotionMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.onMotionChange = () => {
          if (this.reducedMotionMQ.matches) this.teardown();
          else this.enhance();
        };
        this.reducedMotionMQ.addEventListener('change', this.onMotionChange);

        if (!this.reducedMotionMQ.matches) this.enhance();
      }

      // Upgrade the static fallback into the animated marquee.
      enhance() {
        if (this.enhanced || !this.track) return;
        this.enhanced = true;

        this.build();

        // Re-measure once any logo image finishes (or fails) loading — width is
        // unknown before load for color-mode <img>.
        this.track.querySelectorAll('img').forEach((img) => {
          if (!img.complete) {
            img.addEventListener('load', this.onResize, { once: true });
            img.addEventListener('error', this.onResize, { once: true });
          }
        });

        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this);

        // Pause when scrolled out of view (perf).
        this.intersectionObserver = new IntersectionObserver((entries) => {
          this.offscreen = !entries[0].isIntersecting;
          this.applyPlayState();
        });
        this.intersectionObserver.observe(this);

        if (this.pauseHover) {
          this.onEnter = () => {
            this.hovered = true;
            this.applyPlayState();
          };
          this.onLeave = () => {
            this.hovered = false;
            this.applyPlayState();
          };
          this.addEventListener('mouseenter', this.onEnter);
          this.addEventListener('mouseleave', this.onLeave);
        }

        this.onFocusIn = () => {
          this.focused = true;
          this.classList.add('jk-logo-bar__marquee--focus');
          this.applyPlayState();
        };
        this.onFocusOut = (event) => {
          if (event.relatedTarget && this.contains(event.relatedTarget)) return;
          this.focused = false;
          this.classList.remove('jk-logo-bar__marquee--focus');
          (this.viewport || this).scrollLeft = 0;
          this.applyPlayState();
        };
        this.addEventListener('focusin', this.onFocusIn);
        this.addEventListener('focusout', this.onFocusOut);
      }

      // Restore the static, accessible fallback: no clones, no animation.
      teardown() {
        if (!this.enhanced) return;
        this.enhanced = false;
        clearTimeout(this.debounceTimer);
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
          this.resizeObserver = null;
        }
        if (this.intersectionObserver) {
          this.intersectionObserver.disconnect();
          this.intersectionObserver = null;
        }
        if (this.onEnter) this.removeEventListener('mouseenter', this.onEnter);
        if (this.onLeave) this.removeEventListener('mouseleave', this.onLeave);
        if (this.onFocusIn) this.removeEventListener('focusin', this.onFocusIn);
        if (this.onFocusOut) this.removeEventListener('focusout', this.onFocusOut);
        this.focused = false;
        this.hovered = false;
        this.classList.remove('jk-logo-bar__marquee--focus');
        (this.viewport || this).scrollLeft = 0;
        this.classList.remove('jk-logo-bar__marquee--ready');
        this.track.style.animationDuration = '';
        this.track.style.animationPlayState = '';
        this.track.style.willChange = '';
        if (this.originalHTML != null) this.track.innerHTML = this.originalHTML;
      }

      build() {
        if (!this.track || !this.enhanced) return;
        // A resize/load rebuild must also respect reduced motion.
        if (this.reducedMotionMQ.matches) {
          this.teardown();
          return;
        }

        // Reset to the authored set and measure each item's natural width
        // (sum of offsetWidths is wrap-independent).
        this.track.innerHTML = this.originalHTML;
        const items = Array.from(this.track.children);
        let setWidth = 0;
        items.forEach((el) => {
          setWidth += el.offsetWidth;
        });

        const containerWidth = (this.viewport || this).clientWidth;
        if (setWidth <= 0 || containerWidth <= 0) return; // retry on load/resize

        // Repeat the set until one half is at least as wide as the viewport.
        let repeats = Math.max(1, Math.ceil(containerWidth / setWidth));
        // Safety cap so a tiny set can't spawn a runaway number of clones.
        const maxRepeats = Math.max(1, Math.floor(200 / this.originalCount));
        if (repeats > maxRepeats) repeats = maxRepeats;

        const half = this.originalHTML.repeat(repeats);
        this.track.innerHTML = half + half;

        // Hide the duplicated logos from assistive tech — only the first
        // authored set carries the accessible names.
        Array.from(this.track.children).forEach((el, index) => {
          if (index >= this.originalCount) {
            el.setAttribute('aria-hidden', 'true');
            el.querySelectorAll('a').forEach((link) => {
              link.setAttribute('tabindex', '-1');
            });
          }
        });

        // Constant speed: duration = distance (one half) / px-per-second.
        const halfWidth = setWidth * repeats;
        this.track.style.animationDuration = `${halfWidth / this.speed}s`;
        this.track.style.animationDirection = this.reverse ? 'reverse' : 'normal';

        this.classList.add('jk-logo-bar__marquee--ready');
        this.focused = this.contains(document.activeElement);
        if (!this.focused) this.classList.remove('jk-logo-bar__marquee--focus');
        this.applyPlayState();
      }

      applyPlayState() {
        if (!this.track) return;
        const paused = this.hovered || this.offscreen || this.focused;
        this.track.style.animationPlayState = paused ? 'paused' : 'running';
        // Release the compositor layer while offscreen; keep it only while the
        // band is actually on screen. (Hover is transient — don't churn it.)
        this.track.style.willChange = this.offscreen ? 'auto' : 'transform';
      }

      debounce(fn, wait) {
        return () => {
          clearTimeout(this.debounceTimer);
          this.debounceTimer = setTimeout(fn, wait);
        };
      }

      disconnectedCallback() {
        clearTimeout(this.debounceTimer);
        if (this.reducedMotionMQ) {
          this.reducedMotionMQ.removeEventListener('change', this.onMotionChange);
        }
        this.teardown();
      }
    }
  );
}
